import { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AppShell } from './components/shell/AppShell';
import { features } from './features';
import { PrivateRoutes } from './utils/PrivateRoutes';
import { PageNotFound } from './utils/PageNotFound';
import { RouteError } from './utils/RouteError';

const loadHome = () => import('./pages/Home');
const loadAccount = () => import('./pages/Account');
const loadAdmin = () => import('./pages/Admin');
const loadPrivacy = () => import('./pages/Privacy');
const loadPricing = () => import('./pages/Pricing');
const loadSearch = () => import('./pages/Search');
const loadWorkspace = () => import('./pages/Workspace');
// Experiment (run003): session wrap-up
const loadWrapUp = () => import('./features/wrapup/WrapUpPage');

const Home = lazy(loadHome);
const Account = lazy(loadAccount);
const Admin = lazy(loadAdmin);
const Search = lazy(loadSearch);
const Workspace = lazy(loadWorkspace);
const WrapUp = lazy(loadWrapUp);
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Pricing = lazy(loadPricing);
const Privacy = lazy(() => loadPrivacy().then(m => ({ default: m.Privacy })));

export const router = createBrowserRouter([
    {
        element: <AppShell />,
        children: [{
            // A page that throws shows a calm "Reload" screen inside the app, not the router's developer page
            errorElement: <RouteError />,
            children: [
                { path: '/', element: <Home /> },
                { path: '/login', element: <Login /> },
                { path: '/privacy', element: <Privacy /> },
                { path: '/pricing', element: <Pricing /> },
                {
                    element: <PrivateRoutes />,
                    children: [
                        { path: '/account', element: <Account /> },
                        { path: '/admin', element: <Admin /> },
                        { path: '/search', element: <Search /> },
                        // One page with tabs (/w/:slug, /w/:slug/members): switching tabs doesn't replay the page's entrance
                        { path: '/w/:slug/:tab?', element: <Workspace />, handle: { page: 'workspace' } },
                    ],
                },
                // Like the spaces it sums up, a wrap-up opens by link; each part goes through its space's access check
                { path: '/wrap', element: <WrapUp /> },
                // Board, retro and goal space pages are reachable without signing in (view-only by link)
                ...features.flatMap(f => f.routes),
                { path: '*', element: <PageNotFound /> },
            ],
        }],
    },
]);

/**
 * Download every page's code once the browser is idle after the first page has loaded, so later
 * navigations never wait on a chunk download. Failures are ignored: the page will load on demand.
 */
export function preloadRoutes() {
    const loaders: Array<() => Promise<unknown>> = [loadHome, loadAccount, loadPrivacy, loadPricing, loadAdmin, loadSearch, ...features.flatMap(f => (f.preload ? [f.preload] : []))];
    const run = () => loaders.forEach(load => load().catch(() => { }));
    const idle = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    if (idle) idle(run, { timeout: 4000 });
    else setTimeout(run, 2000);
}
